import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Otter Assist admin — provider spend tracking against the monthly ceiling.
// The ceiling WARNS only: nothing is ever stopped or throttled. Every figure is an estimate
// derived from recorded usage, never a billed amount. Super Admin only (provider_cost).

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

export function OaSpend() {
  const [days, setDays] = useState(14);
  const { data, isError } = useQuery({
    queryKey: ["oa-admin-spend", days],
    queryFn: async () => (await api.get(`/admin/otter-assist/spend?days=${days}`)).data,
    refetchInterval: 120000,
  });

  if (isError) {
    return <p className="text-[12px] text-muted-foreground" data-testid="oa-spend-error">
      Spend tracking is unavailable right now.
    </p>;
  }
  if (!data) return null;

  const ceiling = Number(data.ceiling_usd || 0);
  const pct = ceiling ? Math.min(100, (Number(data.month_to_date_usd) / ceiling) * 100) : 0;
  const warn = data.state === "warning";
  const over = data.state === "critical";
  const barTone = over ? "bg-red-600" : warn ? "bg-amber-500" : "bg-teal-600";
  const peak = Math.max(...(data.daily || []).map((d) => Number(d.estimated_usd) || 0), 0.000001);

  return (
    <Card className="p-4" data-testid="oa-admin-spend">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-[640] text-foreground">Provider spend</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {data.basis}. The monthly ceiling is a warning only — Otter Assist is never stopped
            when it is reached.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {[7, 14, 30].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"}
              className="h-7 px-2.5 text-[11px]" onClick={() => setDays(d)}
              data-testid={`oa-spend-range-${d}`}>{d}d</Button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div>
          <div className="text-[10px] font-[700] uppercase tracking-wider text-muted-foreground">
            Today ({data.today_calls} calls)
          </div>
          <div className="text-2xl font-[680] tabular-nums" data-testid="oa-spend-today">
            {usd(data.today_usd)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-[700] uppercase tracking-wider text-muted-foreground">
            Month to date ({data.month})
          </div>
          <div className="text-2xl font-[680] tabular-nums" data-testid="oa-spend-mtd">
            {usd(data.month_to_date_usd)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-[700] uppercase tracking-wider text-muted-foreground">
            Monthly ceiling
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-[680] tabular-nums" data-testid="oa-spend-ceiling">
              {ceiling ? usd(ceiling) : "off"}
            </span>
            <Badge variant="outline" className="text-[10px]" data-testid="oa-spend-ceiling-source">
              {data.ceiling_source}
            </Badge>
          </div>
        </div>
      </div>

      {ceiling ? (
        <div className="mt-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className={`h-full rounded-full transition-[width] duration-500 ${barTone}`}
              style={{ width: `${pct}%` }} data-testid="oa-spend-bar" />
          </div>
          <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
            <span data-testid="oa-spend-utilisation">
              {Math.round(pct)}% of the monthly ceiling used
            </span>
            <span>projected month end {usd(data.projected_month_end_usd ?? 0)}</span>
          </div>
        </div>
      ) : null}

      {(warn || over) && (
        <p role="status" aria-live="polite"
          className={`mt-3 flex items-start gap-1.5 rounded-md border px-3 py-2 text-[11px] font-[600] ${
            over ? "border-red-300 bg-red-50 text-red-800"
              : "border-amber-300 bg-amber-50 text-amber-900"}`}
          data-testid="oa-spend-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {over
            ? `Estimated spend this month has reached the ${usd(ceiling)} ceiling. Nothing has been stopped — review provider usage or raise the ceiling.`
            : `Estimated spend this month is above ${Math.round((data.warn_ratio || 0.8) * 100)}% of the ${usd(ceiling)} ceiling.`}
        </p>
      )}

      <div className="mt-4">
        <div className="text-[10px] font-[700] uppercase tracking-wider text-muted-foreground">
          Daily spend (UTC)
        </div>
        <ul className="mt-2 divide-y divide-border" data-testid="oa-spend-daily">
          {(data.daily || []).map((d) => (
            <li key={d.day} className="flex items-center gap-3 py-1.5 text-[12px]"
              data-testid={`oa-spend-day-${d.day}`}>
              <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{d.day}</span>
              <span className="h-1.5 rounded-full bg-teal-600/70"
                style={{ width: Number(d.estimated_usd)
                  ? `${Math.max(2, (Number(d.estimated_usd) / peak) * 60)}%` : "0%" }} />
              <span className="ml-auto tabular-nums text-muted-foreground">{d.calls} calls</span>
              <span className="w-16 shrink-0 text-right font-[640] tabular-nums">
                {usd(d.estimated_usd)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

export default OaSpend;
